import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
API.interceptors.request.use(cfg => {
  try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {}
  return cfg;
});

const ATTRS = ['sweet','salty','sour','bitter','umami','heat','aroma_intensity'];
const LABELS = { sweet:'Sweet', salty:'Salty', sour:'Sour', bitter:'Bitter', umami:'Umami', heat:'Heat', aroma_intensity:'Aroma' };

export default function FlavorProfileTab({ product }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!product?.product_code) return;
    API.get('/flavor/resolve/' + encodeURIComponent(product.product_code))
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.error || 'Failed to resolve flavor'));
  }, [product?.product_code]);

  if (err) return <div className="card"><div style={{ color: 'var(--red)' }}>{err}</div></div>;
  if (!data) return <div className="card">Loading flavor profile…</div>;
  if (!data.profile) return <div className="card">No recipe found — flavor is computed from the recipe.</div>;

  const p = data.profile;
  const max = 10;

  // Simple spider chart via SVG
  const cx = 130, cy = 130, R = 100;
  const pts = ATTRS.map((a, i) => {
    const ang = (Math.PI * 2 * i) / ATTRS.length - Math.PI / 2;
    const r = ((p[a] || 0) / max) * R;
    return [cx + r * Math.cos(ang), cy + r * Math.sin(ang)];
  });
  const axisPts = ATTRS.map((a, i) => {
    const ang = (Math.PI * 2 * i) / ATTRS.length - Math.PI / 2;
    return { x: cx + R * Math.cos(ang), y: cy + R * Math.sin(ang), lx: cx + (R + 18) * Math.cos(ang), ly: cy + (R + 18) * Math.sin(ang), label: LABELS[a], val: p[a] };
  });

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div className="card" style={{ width: 320 }}>
        <div className="card-title">👅 Resolved Flavor Profile</div>
        <svg width="260" height="260" viewBox="0 0 260 260">
          {[0.25, 0.5, 0.75, 1].map(f => (
            <polygon key={f} points={ATTRS.map((a, i) => {
              const ang = (Math.PI * 2 * i) / ATTRS.length - Math.PI / 2;
              return (cx + R * f * Math.cos(ang)) + ',' + (cy + R * f * Math.sin(ang));
            }).join(' ')} fill="none" stroke="var(--border, #ddd)" strokeWidth="1" />
          ))}
          {axisPts.map((ap, i) => <line key={i} x1={cx} y1={cy} x2={ap.x} y2={ap.y} stroke="var(--border, #ddd)" strokeWidth="1" />)}
          <polygon points={pts.map(pt => pt.join(',')).join(' ')} fill="rgba(123,45,107,0.25)" stroke="var(--accent, #7B2D6B)" strokeWidth="2" />
          {axisPts.map((ap, i) => (
            <text key={i} x={ap.lx} y={ap.ly} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 10, fill: 'var(--text, #333)' }}>
              {ap.label} {Number(ap.val || 0).toFixed(1)}
            </text>
          ))}
        </svg>
        {!data.known && <div style={{ fontSize: 11, color: 'var(--orange, #e65100)' }}>⚠ Some components lack flavor data — profile is partial.</div>}
      </div>

      <div className="card" style={{ flex: 1, minWidth: 300 }}>
        <div className="card-title">Computed from recipe</div>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-elevated)' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Component</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: 80 }}>Fraction</th>
              <th style={{ textAlign: 'center', padding: '6px 8px', width: 100 }}>Source</th>
            </tr>
          </thead>
          <tbody>
            {(data.components || []).map((c, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 8px' }}>{c.item_name || c.item_code}</td>
                <td style={{ textAlign: 'right', padding: '6px 8px' }}>{((c.fraction || 0) * 100).toFixed(1)}%</td>
                <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                  {c.known
                    ? <span style={{ fontSize: 10, background: '#e8f5e9', color: '#2e7d32', borderRadius: 8, padding: '2px 8px' }}>{c.source}</span>
                    : <span style={{ fontSize: 10, background: '#fff3e0', color: '#e65100', borderRadius: 8, padding: '2px 8px' }}>no data</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
